import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import {
  Box, Typography, TextField, InputAdornment, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Drawer, Divider, alpha,
} from '@mui/material';
import {
  Login as LoginIcon,
  Logout as LogoutIcon,
  Close as CloseIcon,
  EditCalendar as EditCalendarIcon,
  OpenInNew as OpenInNewIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CalendarMonth as CalendarMonthIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

import { TableListPagination } from '../../common/TableList';
import { tableListThSx, tableNavButtonSx } from '../../common/SaaSTokens';
import {
  C, R, FONT_UI, FONT_MONO, FONT_TITLE, TRANSITION,
  getStatusMeta,
  StatusBadge, UserAvatarCell, DrawerInfoRow, inputSx,
  AttendanceTableShell,
} from './AttendanceSharedUI';
import { formatMalaysiaAttendanceClock } from '../../../utils/malaysiaTime';

const MotionBox = motion(Box);
const MotionTableRow = motion(TableRow);

// ─── Design tokens ────────────────────────────────────────────────────────────
const B = {
  50:  '#EFF6FF', 100: '#DBEAFE', 200: '#BFDBFE',
  300: '#93C5FD', 400: '#60A5FA', 500: '#3B82F6',
  600: '#2563EB', 700: '#1D4ED8', 800: '#1E40AF', 900: '#1E3A8A',
};

// Per-user avatar colors — deterministic by index
const AVATAR_PALETTES = [
  { bg: alpha('#1e40af', 0.18), color: '#93c5fd', border: alpha('#1e40af', 0.35) },
  { bg: alpha('#7c3aed', 0.18), color: '#c4b5fd', border: alpha('#7c3aed', 0.35) },
  { bg: alpha('#0f766e', 0.18), color: '#5eead4', border: alpha('#0f766e', 0.35) },
  { bg: alpha('#b45309', 0.18), color: '#fcd34d', border: alpha('#b45309', 0.35) },
  { bg: alpha('#9d174d', 0.18), color: '#f9a8d4', border: alpha('#9d174d', 0.35) },
  { bg: alpha('#065f46', 0.18), color: '#6ee7b7', border: alpha('#065f46', 0.35) },
  { bg: alpha('#1e3a8a', 0.18), color: '#a5b4fc', border: alpha('#1e3a8a', 0.35) },
  { bg: alpha('#7f1d1d', 0.18), color: '#fca5a5', border: alpha('#7f1d1d', 0.35) },
];

function getInitials(name = '') {
  return name.split(' ').map((n) => n[0]).join('').substring(0, 2).toUpperCase();
}

// ─── Colourful avatar ─────────────────────────────────────────────────────────
const ColorAvatar = ({ user, index, size = 36 }) => {
  const p = AVATAR_PALETTES[index % AVATAR_PALETTES.length];
  return (
    <Box sx={{
      width: size,
      height: size,
      borderRadius: '10px',
      background: p.bg,
      border: `1.5px solid ${p.border}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      color: p.color,
      fontSize: size * 0.33,
      fontWeight: 700,
      fontFamily: FONT_UI,
      letterSpacing: '0.5px',
    }}>
      {getInitials(user?.name || user?.full_name || '')}
    </Box>
  );
};

// ─── Status badge (pill with dot) ─────────────────────────────────────────────
const PillBadge = ({ attendance }) => {
  const meta = getStatusMeta(attendance);
  if (!meta) return null;

  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      px: '10px', py: '4px',
      borderRadius: '999px',
      fontSize: 11, fontWeight: 700, letterSpacing: '0.3px',
      color: meta.fg, background: meta.bg, border: `1px solid ${meta.border}`,
      fontFamily: FONT_UI, whiteSpace: 'nowrap',
    }}>
      <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: meta.fg, flexShrink: 0 }} />
      {meta.label}
    </Box>
  );
};

// ─── Monospaced time cell ─────────────────────────────────────────────────────
const TimeCell = ({ value }) => (
  <Typography sx={{
    fontSize: 12.5,
    fontFamily: FONT_MONO,
    letterSpacing: '0.3px',
    color: value ? '#f1f5f9' : 'rgba(100,116,139,0.45)',
    fontWeight: value ? 500 : 400,
  }}>
    {value ? formatMalaysiaAttendanceClock(value) : '—'}
  </Typography>
);

// ─── Action icon button ───────────────────────────────────────────────────────
const ActionBtn = ({ title, icon: Icon, onClick, hoverColor = B[400], restColor, disabled = false }) => {
  const rc = restColor || hoverColor;
  return (
    <Tooltip title={title} arrow>
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          disabled={disabled}
          sx={{
            width: 30,
            height: 30,
            borderRadius: '8px',
            color: disabled ? alpha('#94a3b8', 0.25) : alpha(rc, 0.75),
            bgcolor: alpha(rc, 0.1),
            border: `1px solid ${alpha(rc, 0.2)}`,
            transition: 'all 0.15s ease',
            '&:hover': {
              bgcolor: alpha(hoverColor, 0.2),
              color: hoverColor,
              borderColor: alpha(hoverColor, 0.35),
            },
            '&.Mui-disabled': { opacity: 0.3 },
          }}
        >
          <Icon sx={{ fontSize: 15 }} />
        </IconButton>
      </span>
    </Tooltip>
  );
};

// ─── Quick-action labelled button (drawer) ────────────────────────────────────
const QABtn = ({ icon: Icon, label, onClick, hoverColor = B[400] }) => (
  <Box
    component="button"
    onClick={onClick}
    sx={{
      display: 'flex', alignItems: 'center', gap: '6px',
      height: 34, px: '14px',
      borderRadius: '8px',
      border: '1px solid rgba(255,255,255,0.1)',
      background: 'rgba(30,41,59,0.6)',
      color: 'rgba(148,163,184,0.9)',
      fontSize: 12, fontWeight: 500, fontFamily: FONT_UI,
      cursor: 'pointer',
      transition: 'all 0.15s',
      '&:hover': {
        background: alpha(hoverColor, 0.12),
        color: hoverColor,
        borderColor: alpha(hoverColor, 0.3),
      },
    }}
  >
    <Icon sx={{ fontSize: 14 }} />
    {label}
  </Box>
);

// ─── DailyAttendance ──────────────────────────────────────────────────────────
const DailyAttendance = ({
  users = [], attendanceData = [], currentDate, dateStrings, permissions,
  onDateChange, onSelectDate, onCheckIn, onCheckOut, onAttendanceClick,
}) => {
  const navigate = useNavigate();
  const [page,        setPage]        = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [drawerUser,  setDrawerUser]  = useState(null);
  const dateInputRef = useRef(null);

  const getRecord = useCallback(
    (uid) => attendanceData.find((r) => r.user_id === uid) || null,
    [attendanceData],
  );

  // Pre-filtered by parent Attendance page (search, department, status).
  const filteredUsers = users;

  useEffect(() => { setPage(0); }, [filteredUsers]);

  const paginated = useMemo(
    () => filteredUsers.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [filteredUsers, page, rowsPerPage],
  );

  const drawerRecord = drawerUser ? getRecord(drawerUser.id) : null;
  const isToday      = dateStrings?.current === dateStrings?.today;
  const canUpdate    = permissions?.canUpdateAttendance?.() ?? false;
  const canStatus    = permissions?.canUpdateStatus?.() ?? false;

  // ─── Improved thSx with better vertical padding ───────────────────────────
  const thSx = {
    ...tableListThSx,
    px: { xs: 2, sm: 3 },
    py: 1.5,
    fontSize: '0.7rem',
    letterSpacing: '0.6px',
    '&:first-of-type': { pl: { xs: 2, sm: '24px' } },
    '&:last-of-type':  { pr: { xs: 2, sm: '24px' } },
  };

  const dateNavigator = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
      <Tooltip title="Previous day" arrow>
        <span>
          <IconButton size="small" onClick={() => onDateChange(-1)} sx={tableNavButtonSx(false)}>
            <ChevronLeftIcon />
          </IconButton>
        </span>
      </Tooltip>

      <TextField
        inputRef={dateInputRef}
        type="date"
        size="small"
        value={dateStrings?.current}
        onChange={(e) => onSelectDate?.(e.target.value)}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => {
                  const el = dateInputRef.current;
                  if (!el) return;
                  if (typeof el.showPicker === 'function') el.showPicker();
                  else el.focus();
                }}
                sx={{
                  color: alpha('#E2E8F0', 0.7),
                  '&:hover': { color: '#fff', bgcolor: alpha('#E2E8F0', 0.08) },
                }}
              >
                <CalendarMonthIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          ...inputSx,
          width: 148,
          '& .MuiInputBase-input': { colorScheme: 'dark', fontSize: 12, fontFamily: FONT_MONO },
          '& input[type="date"]::-webkit-calendar-picker-indicator': {
            opacity: 0, position: 'absolute', right: 0, width: '100%', height: '100%', cursor: 'pointer',
          },
        }}
      />

      <Tooltip title="Next day" arrow>
        <span>
          <IconButton size="small" onClick={() => onDateChange(1)} sx={tableNavButtonSx(false)}>
            <ChevronRightIcon />
          </IconButton>
        </span>
      </Tooltip>

      {isToday && (
        <Box sx={{
          px: '9px', py: '3px', borderRadius: '999px',
          fontSize: 9.5, fontWeight: 800, fontFamily: FONT_UI, letterSpacing: '0.8px',
          color: B[400], bgcolor: alpha(B[500], 0.12),
          border: `1px solid ${alpha(B[400], 0.3)}`,
          textTransform: 'uppercase',
          display: 'flex', alignItems: 'center',
        }}>
          Today
        </Box>
      )}
    </Box>
  );

  return (
    <AttendanceTableShell
      streamLabel="Daily Roster"
      subtitle="Check-in activity and attendance status"
      recordCount={filteredUsers.length}
      headerRight={dateNavigator}
    >
      {/* ── Table ── */}
      <TableContainer sx={{
        width: '100%',
        overflowX: 'auto',
        '&::-webkit-scrollbar': { height: 6 },
        '&::-webkit-scrollbar-thumb': { background: alpha('#38bdf8', 0.28), borderRadius: 4 },
      }}>
        <Table stickyHeader size="small" sx={{ minWidth: 650, tableLayout: 'auto', width: '100%' }}>
          <TableHead>
            <TableRow>
              {[
                { label: 'Member',    w: '30%', align: 'left'   },
                { label: 'Check-in',  w: '16%', align: 'center' },
                { label: 'Check-out', w: '16%', align: 'center' },
                { label: 'Status',    w: '20%', align: 'center' },
                { label: 'Actions',   w: '18%', align: 'right'  },
              ].map((col) => (
                <TableCell key={col.label} align={col.align} sx={{ ...thSx, width: col.w }}>
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} sx={{ border: 'none', py: 10, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 13, color: 'rgba(100,116,139,0.8)', fontFamily: FONT_UI }}>
                    No members match your filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : paginated.map((user, ri) => {
              const rec    = getRecord(user.id);
              const absIdx = page * rowsPerPage + ri;
              const zebra  = absIdx % 2 === 1;

              return (
                <MotionTableRow
                  key={user.id}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.16, delay: ri * 0.025 }}
                  hover
                  onClick={() => setDrawerUser(user)}
                  sx={{
                    cursor: 'pointer',
                    transition: 'background 0.12s',
                    bgcolor: zebra ? alpha('#1e293b', 0.38) : 'transparent',
                    '&:hover': { bgcolor: alpha('#38bdf8', 0.06) },
                    '& .MuiTableCell-root': {
                      borderBottom: `1px solid ${alpha('#f8fafc', 0.05)}`,
                      // ─── IMPROVED: more generous vertical + horizontal padding ──
                      py: { xs: 1.5, sm: 1.75 },
                      px: { xs: 2, sm: 3 },
                      fontFamily: FONT_UI,
                      fontSize: '0.84rem',
                      color: alpha('#f8fafc', 0.82),
                      verticalAlign: 'middle',
                    },
                  }}
                >
                  {/* Member */}
                  <TableCell sx={{ pl: { xs: '16px !important', sm: '24px !important' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <ColorAvatar user={user} index={absIdx} size={36} />
                      <Box>
                        <Typography sx={{
                          fontSize: 13.5,
                          fontWeight: 600,
                          color: '#f1f5f9',
                          fontFamily: FONT_UI,
                          lineHeight: 1.25,
                        }}>
                          {user.name || user.full_name}
                        </Typography>
                        {user.department?.name && (
                          <Typography sx={{
                            fontSize: 11,
                            color: 'rgba(100,116,139,0.85)',
                            fontFamily: FONT_UI,
                            mt: 0.3,
                          }}>
                            {user.department.name}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>

                  {/* Check-in */}
                  <TableCell align="center">
                    <TimeCell value={rec?.check_in_time} />
                  </TableCell>

                  {/* Check-out */}
                  <TableCell align="center">
                    <TimeCell value={rec?.check_out_time} />
                  </TableCell>

                  {/* Status */}
                  <TableCell align="center">
                    <PillBadge attendance={rec} />
                  </TableCell>

                  {/* Actions */}
                  <TableCell
                    align="right"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ pr: { xs: '16px !important', sm: '24px !important' } }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                      {isToday && canUpdate && !rec?.check_in_time && (
                        <ActionBtn
                          title="Check in"
                          icon={LoginIcon}
                          onClick={() => onCheckIn(user.id)}
                          hoverColor="#10b981"
                          restColor="#10b981"
                        />
                      )}
                      {isToday && canUpdate && rec?.check_in_time && !rec?.check_out_time && (
                        <ActionBtn
                          title="Check out"
                          icon={LogoutIcon}
                          onClick={() => onCheckOut(user.id)}
                          hoverColor="#f59e0b"
                          restColor="#f59e0b"
                        />
                      )}
                      {!isToday && (
                        <ActionBtn
                          title={canStatus ? 'Cycle status' : 'No permission'}
                          icon={EditCalendarIcon}
                          onClick={() => onAttendanceClick(user.id, dateStrings.current)}
                          hoverColor={C.purple}
                          restColor={C.purple}
                          disabled={!canStatus}
                        />
                      )}
                      <ActionBtn
                        title="View profile"
                        icon={OpenInNewIcon}
                        onClick={() => navigate(`/users/details/${user.id}`)}
                        hoverColor={B[400]}
                        restColor={B[400]}
                      />
                    </Box>
                  </TableCell>
                </MotionTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TableListPagination
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={filteredUsers.length}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
      />

      {/* ── Detail drawer ── */}
      <Drawer
        anchor="right"
        open={Boolean(drawerUser)}
        onClose={() => setDrawerUser(null)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 340 },
            background: 'rgba(11,17,32,0.98)',
            borderLeft: '1px solid rgba(255,255,255,0.08)',
            backdropFilter: 'blur(24px)',
          },
        }}
      >
        <AnimatePresence>
          {drawerUser && (
            <MotionBox
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
            >
              {/* Drawer header */}
              <Box sx={{
                px: 3,
                py: 2.5,
                borderBottom: '1px solid rgba(255,255,255,0.08)',
                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                background: 'linear-gradient(135deg, rgba(59,130,246,0.06) 0%, rgba(167,139,250,0.04) 100%)',
              }}>
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9', fontFamily: FONT_UI }}>
                    Member Details
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: 'rgba(100,116,139,1)', fontFamily: FONT_UI, mt: 0.3 }}>
                    {currentDate?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setDrawerUser(null)}
                  sx={{
                    width: 28, height: 28, borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.1)',
                    bgcolor: 'rgba(30,41,59,0.6)',
                    color: 'rgba(148,163,184,0.8)',
                    '&:hover': { color: '#f1f5f9', bgcolor: 'rgba(30,41,59,0.9)' },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Box>

              {/* Drawer body */}
              <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 0, overflowY: 'auto' }}>
                {/* Avatar + name */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, mb: 3 }}>
                  <ColorAvatar user={drawerUser} index={users.indexOf(drawerUser)} size={48} />
                  <Box>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9', fontFamily: FONT_UI }}>
                      {drawerUser.name || drawerUser.full_name}
                    </Typography>
                    {drawerUser.department?.name && (
                      <Typography sx={{ fontSize: 11.5, color: 'rgba(100,116,139,0.9)', fontFamily: FONT_UI, mt: 0.3 }}>
                        {drawerUser.department.name}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', mb: 2.5 }} />

                {/* Section label */}
                <Typography sx={{
                  fontSize: 10, fontWeight: 800, color: 'rgba(100,116,139,0.85)',
                  fontFamily: FONT_UI, textTransform: 'uppercase', letterSpacing: '1px', mb: 1.25,
                }}>
                  Attendance Record
                </Typography>

                {/* Record card */}
                <Box sx={{
                  bgcolor: 'rgba(15,23,42,0.6)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  mb: 2.5,
                }}>
                  {[
                    { label: 'Check-in',         value: drawerRecord?.check_in_time ? formatMalaysiaAttendanceClock(drawerRecord.check_in_time) : 'Not checked in',  mono: true  },
                    { label: 'Check-out',         value: drawerRecord?.check_out_time ? formatMalaysiaAttendanceClock(drawerRecord.check_out_time) : 'Not checked out', mono: true  },
                    { label: 'Last updated by',   value: drawerRecord?.last_updater?.name || (drawerRecord?.updated_by ? `User #${drawerRecord.updated_by}` : '—') },
                    { label: 'Absence reason',    value: drawerRecord?.absence_reason || '—' },
                    { label: 'Record updated at', value: drawerRecord?.updated_at || '—', mono: true },
                  ].map((row, i, arr) => (
                    <Box
                      key={row.label}
                      sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        px: 2, py: 1.25,
                        borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                        gap: 1,
                      }}
                    >
                      <Typography sx={{ fontSize: 12, color: 'rgba(100,116,139,0.9)', fontFamily: FONT_UI, flexShrink: 0 }}>
                        {row.label}
                      </Typography>
                      <Typography sx={{
                        fontSize: row.mono ? 11.5 : 12,
                        color: '#f1f5f9', fontWeight: 500,
                        fontFamily: row.mono ? FONT_MONO : FONT_UI,
                        textAlign: 'right',
                      }}>
                        {row.value}
                      </Typography>
                    </Box>
                  ))}

                  {/* Status row */}
                  <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    px: 2, py: 1.25,
                    borderTop: '1px solid rgba(255,255,255,0.05)',
                  }}>
                    <Typography sx={{ fontSize: 12, color: 'rgba(100,116,139,0.9)', fontFamily: FONT_UI }}>
                      Status
                    </Typography>
                    <PillBadge attendance={drawerRecord} />
                  </Box>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', mb: 2.5 }} />

                {/* Quick actions */}
                <Typography sx={{
                  fontSize: 10, fontWeight: 800, color: 'rgba(100,116,139,0.85)',
                  fontFamily: FONT_UI, textTransform: 'uppercase', letterSpacing: '1px', mb: 1.25,
                }}>
                  Quick Actions
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }} onClick={(e) => e.stopPropagation()}>
                  {isToday && canUpdate && !drawerRecord?.check_in_time && (
                    <QABtn
                      icon={LoginIcon}
                      label="Check in"
                      onClick={() => { onCheckIn(drawerUser.id); setDrawerUser(null); }}
                      hoverColor="#10b981"
                    />
                  )}
                  {isToday && canUpdate && drawerRecord?.check_in_time && !drawerRecord?.check_out_time && (
                    <QABtn
                      icon={LogoutIcon}
                      label="Check out"
                      onClick={() => { onCheckOut(drawerUser.id); setDrawerUser(null); }}
                      hoverColor="#f59e0b"
                    />
                  )}
                  {!isToday && (
                    <QABtn
                      icon={EditCalendarIcon}
                      label="Update status"
                      onClick={() => onAttendanceClick(drawerUser.id, dateStrings.current)}
                      hoverColor={C.purple}
                    />
                  )}
                  <QABtn
                    icon={OpenInNewIcon}
                    label="Face scan page"
                    onClick={() => navigate('/attendance/face-scan')}
                    hoverColor={B[400]}
                  />
                </Box>
              </Box>
            </MotionBox>
          )}
        </AnimatePresence>
      </Drawer>
    </AttendanceTableShell>
  );
};

export default DailyAttendance;
